import { toast } from "react-toastify"
import axios from "axios"
import moment from "moment"

// Action Types
export const SET_PO_LOADING = "SET_PO_LOADING"
export const SET_PURCHASE_ORDERS = "SET_PURCHASE_ORDERS"
export const SET_TOTAL_PURCHASE_ORDERS = "SET_TOTAL_PURCHASE_ORDERS"
export const SET_TOTAL_PO_PAGES = "SET_TOTAL_PO_PAGES"
export const SET_PO_DATE = "SET_PO_DATE"
export const SET_PO_SUPPLIER = "SET_PO_SUPPLIER"
export const SET_PO_COMMENTS = "SET_PO_COMMENTS"
export const SET_PO_REMARKS = "SET_PO_REMARKS"
export const SET_PO_PRODUCTS = "SET_PO_PRODUCTS"
export const CLEAR_NEW_PO = "CLEAR_NEW_PO"
export const SET_PO_IR_NUMBER = "SET_PO_IR_NUMBER"
export const LOAD_PO_FOR_EDIT = "LOAD_PO_FOR_EDIT"

// Get all purchase orders from db
export const getPurchaseOrders = data => {
  return async (dispatch, getState) => {
    const skip = data?.skip || 1
    const limit = data?.limit || 25
    const search = data?.search || ""

    dispatch({
      type: SET_PO_LOADING,
      data: true,
    })

    const token = getState().auth.auth.token

    try {
      const res = await axios.get(
        `${
          process.env.GATSBY_API_CALL
        }/purchase-orders?page=${skip}&limit=${limit}${
          search && "&search=" + encodeURIComponent(search)
        }`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      dispatch({
        type: SET_PURCHASE_ORDERS,
        data: res.data.purchaseOrders,
      })

      dispatch({
        type: SET_TOTAL_PURCHASE_ORDERS,
        data: res.data.totalPurchaseOrders,
      })

      dispatch({
        type: SET_TOTAL_PO_PAGES,
        data: res.data.totalPages,
      })

      dispatch({
        type: SET_PO_LOADING,
        data: false,
      })
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${e.response?.data.error || "Failed to fetch purchase orders"}`
      )
      dispatch({
        type: SET_PO_LOADING,
        data: false,
      })
    }
  }
}

// Save new purchase order to db
export const savePurchaseOrder = () => {
  return async (dispatch, getState) => {
    const token = getState().auth.auth.token
    const poData = getState().purchaseOrder.newPO

    if (!poData.irNumber) {
      toast.error(
        "Purchase Orders can only be created from Internal Requisitions"
      )
      return
    }

    if (!poData.supplierCode) {
      toast.error("Supplier information missing from Internal Requisition")
      return
    }

    if (!poData.poProducts || poData.poProducts.length === 0) {
      toast.error("No products found from Internal Requisition")
      return
    }

    const payload = {
      supplierCode: poData.supplierCode,
      date: poData.date,
      comments: poData.comments,
      remarks: poData.remarks,
      irNumber: poData.irNumber,
      poProducts: poData.poProducts.map(product => ({
        itemCode: product.itemCode,
        description: product.description,
        quantity: product.quantity,
        unitPrice: product.unitPrice,
        discount: product.discount || 0,
        discountType: product.discountType || "%",
      })),
    }

    dispatch({ type: SET_PO_LOADING, data: true })

    try {
      const res = await axios.post(
        `${process.env.GATSBY_API_CALL}/purchase-orders`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Add to existing purchase orders list
      const existingPOs = getState().purchaseOrder.purchaseOrders || 0
      const newPO = res.data.purchaseOrder
      const updatedPoList = [newPO, ...existingPOs]

      dispatch({
        type: SET_PURCHASE_ORDERS,
        data: updatedPoList,
      })

      dispatch({ type: CLEAR_NEW_PO })
      dispatch({ type: SET_PO_LOADING, data: false })

      return res.data.poNumber
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${e.response?.data.error || "Failed to create purchase order"}`
      )
      dispatch({ type: SET_PO_LOADING, data: false })
      throw new Error(
        e.response?.data.error || "Failed to create purchase order"
      )
    }
  }
}

// Approve Purchase Order
export const approvePurchaseOrder = id => {
  return async (dispatch, getState) => {
    const token = getState().auth.auth.token

    try {
      const res = await axios.patch(
        `${process.env.GATSBY_API_CALL}/purchase-orders/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Update the purchase order in the list
      const updatedPurchaseOrders = getState().purchaseOrder.purchaseOrders.map(
        po => (po._id === id ? res.data : po)
      )

      dispatch({
        type: SET_PURCHASE_ORDERS,
        data: updatedPurchaseOrders,
      })

      toast.success("Purchase order approved successfully!")
      return res.data
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${e.response?.data.error || "Failed to approve purchase order"}`
      )
      throw e
    }
  }
}

// Update Purchase Order
export const updatePurchaseOrder = (id, updates) => {
  return async (dispatch, getState) => {
    const token = getState().auth.auth.token

    try {
      const res = await axios.patch(
        `${process.env.GATSBY_API_CALL}/purchase-orders/${id}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Update the purchase order in the list
      const updatedPurchaseOrders = getState().purchaseOrder.purchaseOrders.map(
        po => (po._id === id ? res.data : po)
      )

      dispatch({
        type: SET_PURCHASE_ORDERS,
        data: updatedPurchaseOrders,
      })

      toast.success("Purchase order updated successfully!")
      return res.data
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${e.response?.data.error || "Failed to update purchase order"}`
      )
      throw e
    }
  }
}

// Cancel Purchase Order
export const cancelPurchaseOrder = (id, reason) => {
  return async (dispatch, getState) => {
    const token = getState().auth.auth.token

    try {
      const res = await axios.patch(
        `${process.env.GATSBY_API_CALL}/purchase-orders/${id}/cancel`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Update the purchase order in the list
      const updatedPurchaseOrders = getState().purchaseOrder.purchaseOrders.map(
        po => (po._id === id ? res.data : po)
      )

      dispatch({
        type: SET_PURCHASE_ORDERS,
        data: updatedPurchaseOrders,
      })

      toast.success("Purchase order cancelled successfully!")
      return res.data
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${e.response?.data.error || "Failed to cancel purchase order"}`
      )
      throw e
    }
  }
}

// Delete Purchase Order
export const deletePurchaseOrder = id => {
  return async (dispatch, getState) => {
    const token = getState().auth.auth.token

    if (
      !window.confirm("Are you sure you want to delete this purchase order?")
    ) {
      return
    }

    dispatch({
      type: SET_PO_LOADING,
      data: true,
    })

    try {
      await axios.delete(
        `${process.env.GATSBY_API_CALL}/purchase-orders/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      const updatedPurchaseOrders = getState().purchaseOrder.purchaseOrders.filter(
        po => po._id !== id
      )
      dispatch({
        type: SET_PURCHASE_ORDERS,
        data: updatedPurchaseOrders,
      })

      // Update total count
      dispatch({
        type: SET_TOTAL_PURCHASE_ORDERS,
        data: getState().purchaseOrder.totalPurchaseOrders - 1,
      })

      toast.success("Purchase order deleted successfully!")
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${e.response?.data.error || "Failed to delete purchase order"}`
      )
      dispatch({
        type: SET_PO_LOADING,
        data: false,
      })
    }
  }
}

// Action creators for form management
export const setPODate = date => ({
  type: SET_PO_DATE,
  data: date,
})

export const setPOSupplier = supplierCode => ({
  type: SET_PO_SUPPLIER,
  data: supplierCode,
})

export const setPOComments = comments => ({
  type: SET_PO_COMMENTS,
  data: comments,
})

export const setPORemarks = remarks => ({
  type: SET_PO_REMARKS,
  data: remarks,
})

export const setPOProducts = products => ({
  type: SET_PO_PRODUCTS,
  data: products,
})

export const setPOIRNumber = irNumber => ({
  type: SET_PO_IR_NUMBER,
  data: irNumber,
})

export const clearNewPO = () => ({
  type: CLEAR_NEW_PO,
})

// Add product to PO
export const addPOProduct = product => {
  return (dispatch, getState) => {
    const poProductsFromState = getState().purchaseOrder.newPO.poProducts || []

    const itemExists = poProductsFromState.some(
      p => p.itemCode === product.itemCode
    )

    if (itemExists) {
      toast.error(`Error: Item ${product.itemCode} has already been added.`)
      return
    }

    const newPOProduct = {
      itemCode: product.itemCode,
      description: product.description,
      quantity: product.quantity || 1,
      unitPrice: product.unitPrice || 0,
      discount: product.discount || 0,
      discountType: product.discountType || "%",
    }

    const newPOProducts = [...poProductsFromState, newPOProduct]

    dispatch({
      type: SET_PO_PRODUCTS,
      data: newPOProducts,
    })
  }
}

// Remove product from PO
export const removePOProduct = itemCode => {
  return (dispatch, getState) => {
    const newPOProducts = getState().purchaseOrder.newPO.poProducts.filter(
      product => product.itemCode !== itemCode
    )

    dispatch({
      type: SET_PO_PRODUCTS,
      data: newPOProducts,
    })
  }
}

// Update product quantity in PO
export const updatePOProductQuantity = (itemCode, quantity) => {
  return (dispatch, getState) => {
    const newPOProducts = getState().purchaseOrder.newPO.poProducts.map(
      product => {
        if (product.itemCode === itemCode) {
          return { ...product, quantity: Number(quantity) }
        }
        return product
      }
    )

    dispatch({
      type: SET_PO_PRODUCTS,
      data: newPOProducts,
    })
  }
}

// Update product unit price in PO
export const updatePOProductPrice = (itemCode, unitPrice) => {
  return (dispatch, getState) => {
    const newPOProducts = getState().purchaseOrder.newPO.poProducts.map(
      product => {
        if (product.itemCode === itemCode) {
          return { ...product, unitPrice: Number(unitPrice) }
        }
        return product
      }
    )

    dispatch({
      type: SET_PO_PRODUCTS,
      data: newPOProducts,
    })
  }
}

// Update product discount in PO
export const updatePOProductDiscount = (itemCode, discount) => {
  return (dispatch, getState) => {
    const newPOProducts = getState().purchaseOrder.newPO.poProducts.map(
      product => {
        if (product.itemCode === itemCode) {
          return { ...product, discount: Number(discount) }
        }
        return product
      }
    )

    dispatch({
      type: SET_PO_PRODUCTS,
      data: newPOProducts,
    })
  }
}

// Update product discount type in PO
export const updatePOProductDiscountType = (itemCode, discountType) => {
  return (dispatch, getState) => {
    const newPOProducts = getState().purchaseOrder.newPO.poProducts.map(
      product => {
        if (product.itemCode === itemCode) {
          return { ...product, discountType }
        }
        return product
      }
    )

    dispatch({
      type: SET_PO_PRODUCTS,
      data: newPOProducts,
    })
  }
}

// Create Purchase Order from Internal Requisition
export const createPOFromIR = irData => {
  return (dispatch, getState) => {
    // Set basic PO data from IR
    dispatch(setPODate(new Date().toISOString().split("T")[0]))
    dispatch(setPOSupplier(irData.supplierCode))
    dispatch(setPOIRNumber(irData.irNumber))

    // Convert IR products to PO products
    const poProducts = irData.products.map(product => ({
      itemCode: product.itemCode,
      description: product.description,
      quantity: product.orderQty,
      unitPrice: 0,
      discount: 0,
      discountType: "%",
    }))

    dispatch(setPOProducts(poProducts))
  }
}

// Load Purchase Order for editing
export const loadPOForEdit = purchaseOrder => {
  return dispatch => {
    dispatch(setPODate(moment(purchaseOrder.date).format("YYYY-MM-DD")))
    dispatch(setPOSupplier(purchaseOrder.supplierCode))
    dispatch(setPOComments(purchaseOrder.comments || ""))
    dispatch(setPORemarks(purchaseOrder.remarks || ""))
    dispatch(setPOIRNumber(purchaseOrder.irNumber || ""))
    dispatch(setPOProducts(purchaseOrder.poProducts || []))

    dispatch({
      type: LOAD_PO_FOR_EDIT,
      data: {
        isEditing: true,
        editingId: purchaseOrder._id,
      },
    })
  }
}
